import {
  fetchAwsApi,
  getApiErrorMessage,
  parseApiResponse,
  sanitizeApiBaseUrl,
  sanitizeImageUrl,
} from '../security';
import {
  buildItemPhotoProxyEndpointCandidates,
  buildFunctionsApiEndpointCandidates,
  extractCloudFrontObjectKey,
  fetchFirstAvailableFunctionsApi,
} from '../api';
import { compressImageForUpload } from '../utils/compressImage';

export const NOT_APPLICABLE_COLOR_VARIANT_KEY = 'not_applicable';
export const LEGACY_NULL_COLOR_VARIANT_KEY = '__null_color__';

export interface ColorVariantImageSource {
  file?: File;
  previewUrl: string;
  s3Key?: string;
  objectKey?: string;
  originalSortOrder?: number;
  originalIsPrimary?: boolean;
}

export interface ColorVariantImageInput<TPhoto extends ColorVariantImageSource = ColorVariantImageSource> {
  colorName: string;
  colorCode?: string | null;
  photos: TPhoto[];
}

export interface SavedItemColorVariantImageInput<TPhoto extends ColorVariantImageSource = ColorVariantImageSource> {
  variantId?: string;
  colorName: string;
  colorCode?: string | null;
  price: number;
  sortOrder?: number;
  photos: TPhoto[];
  swatchImage?: TPhoto | null;
  frontImage?: TPhoto | null;
  backImage?: TPhoto | null;
}

interface SavedItemImageSyncOptions {
  mode?: 'append' | 'sync';
  itemId?: string;
  showPublic?: boolean;
}

export function isNotApplicableColorVariantKey(value: string) {
  const normalizedValue = value.trim().toLowerCase();
  return normalizedValue === NOT_APPLICABLE_COLOR_VARIANT_KEY
    || normalizedValue === LEGACY_NULL_COLOR_VARIANT_KEY
    || normalizedValue === 'null';
}

/** Resolves the color-variant map key (color code) for the new add_items format. */
export function resolveColorVariantPayloadKey(
  colorCode: string | null | undefined,
  colorName = ''
) {
  const normalizedCode = colorCode?.trim() ?? '';
  if (normalizedCode && !isNotApplicableColorVariantKey(normalizedCode)) {
    return normalizedCode;
  }

  const normalizedName = colorName.trim();
  if (normalizedName && !isNotApplicableColorVariantKey(normalizedName)) {
    return normalizedName;
  }

  return NOT_APPLICABLE_COLOR_VARIANT_KEY;
}

/** Converts the UI's not-applicable color sentinel to the API's null value. */
export function normalizeNotApplicableColorValue(value: string | null | undefined) {
  return typeof value === 'string' && isNotApplicableColorVariantKey(value) ? null : value;
}

function readBlobAsImageDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onerror = () => reject(new Error('Unable to encode image data.'));
    fileReader.onload = () => {
      const result = typeof fileReader.result === 'string' ? fileReader.result : '';
      if (!result.startsWith('data:image/')) {
        reject(new Error('Encoded image data had an unexpected format.'));
        return;
      }

      resolve(result);
    };
    fileReader.readAsDataURL(blob);
  });
}

async function fetchRemoteImageAsDataUrl(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Unable to download an existing image for item save.');
  }

  const blob = await response.blob();
  const mimeType = (blob.type || '').toLowerCase();
  if (mimeType && !mimeType.startsWith('image/')) {
    throw new Error('A saved item image returned an unexpected file type.');
  }

  return readBlobAsImageDataUrl(blob);
}

async function fetchItemPhotoObjectAsDataUrl(objectKey: string) {
  const response = await fetchFirstAvailableFunctionsApi(
    buildItemPhotoProxyEndpointCandidates(objectKey),
    { method: 'GET' },
    'Unable to load an existing item image for save.'
  );
  if (!response.ok) {
    throw new Error('Unable to download an existing image for item save.');
  }

  const blob = await response.blob();
  const mimeType = (blob.type || '').toLowerCase();
  if (mimeType && !mimeType.startsWith('image/')) {
    throw new Error('A saved item image returned an unexpected file type.');
  }

  return readBlobAsImageDataUrl(blob);
}

export async function ensureImageDataUrl(source: ColorVariantImageSource) {
  if (source.file) {
    return readBlobAsImageDataUrl(source.file);
  }

  const dataUrl = sanitizeImageUrl(source.previewUrl, false, true);
  if (dataUrl.startsWith('data:image/')) {
    return dataUrl;
  }

  const existingObjectKey = getExistingS3ObjectKey(source);
  if (existingObjectKey) {
    return fetchItemPhotoObjectAsDataUrl(existingObjectKey);
  }

  const imageUrl = sanitizeImageUrl(source.previewUrl);
  if (!imageUrl) {
    throw new Error('Unable to resolve an item image for save.');
  }

  return fetchRemoteImageAsDataUrl(imageUrl);
}

export async function buildColorKeyedImagePayload<TPhoto extends ColorVariantImageSource>(
  variants: Array<ColorVariantImageInput<TPhoto>>
) {
  const entries = await Promise.all(
    variants.map(async (variant) => {
      const photos = await Promise.all(
        variant.photos.map(async (photo, index) => ({
          img_data: await ensureImageDataUrl(photo),
          sort_order: index + 1,
        }))
      );

      return [variant.colorName, photos] as const;
    })
  );

  return Object.fromEntries(entries);
}

export interface ApiColorS3ImageEntry {
  s3_key: string;
  is_primary: boolean;
  sort_order: number;
}

interface UploadedItemPhotoResponse {
  objectKey?: string;
  s3_key?: string;
}

// Netlify's 6 MB buffered function limit leaves about 4.5 MB for binary
// request bodies after Base64 encoding. Keep multipart batches below that.
const MAX_ITEM_PHOTO_BATCH_BYTES = 4 * 1024 * 1024;

function slugifyUploadGroupPiece(value: string, fallback: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || fallback;
}

function normalizeS3ObjectKey(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmedValue = value.trim();
  if (!trimmedValue || trimmedValue.startsWith('data:') || trimmedValue.startsWith('blob:')) {
    return '';
  }

  try {
    const parsedUrl = new URL(trimmedValue, window.location.origin);
    const objectKey = parsedUrl.searchParams.get('objectKey') ?? parsedUrl.searchParams.get('s3_key');
    if (objectKey) {
      return objectKey.trim().replace(/^\/+/, '');
    }
  } catch {
    // Fall through to raw object-key handling.
  }

  if (/^https?:\/\//i.test(trimmedValue)) {
    return extractCloudFrontObjectKey(trimmedValue);
  }

  return trimmedValue.replace(/^\/+/, '');
}

function getExistingS3ObjectKey(source: ColorVariantImageSource) {
  return (
    normalizeS3ObjectKey(source.s3Key)
    || normalizeS3ObjectKey(source.objectKey)
    || normalizeS3ObjectKey(source.previewUrl)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function extractSavedItemRecord(payload: unknown, expectedItemId = ''): Record<string, unknown> {
  let currentValue = payload;

  for (let depth = 0; depth < 4; depth += 1) {
    if (typeof currentValue === 'string') {
      try {
        currentValue = JSON.parse(currentValue) as unknown;
        continue;
      } catch {
        break;
      }
    }

    if (!isRecord(currentValue)) {
      break;
    }

    const itemCandidates = [currentValue.items, currentValue.results].find(Array.isArray);
    if (itemCandidates) {
      const records = itemCandidates.filter(isRecord);
      const matchingItem = expectedItemId
        ? records.find((item) => getSavedItemId(item) === expectedItemId)
        : records[0];
      return matchingItem ?? {};
    }

    if (isRecord(currentValue.item)) {
      return currentValue.item;
    }

    if (isRecord(currentValue.data)) {
      currentValue = currentValue.data;
      continue;
    }

    if (isRecord(currentValue.body)) {
      currentValue = currentValue.body;
      continue;
    }

    if (typeof currentValue.body === 'string') {
      currentValue = currentValue.body;
      continue;
    }

    return currentValue;
  }

  return {};
}

function getSavedItemId(item: Record<string, unknown>) {
  return String(item.id ?? item.item_id ?? item.itemId ?? '').trim();
}

function getColorVariants(item: Record<string, unknown>) {
  const candidates = [item.color_variants, item.colorVariants];
  for (const candidate of candidates) {
    const parsedCandidate = parseJsonLikeValue(candidate);
    if (Array.isArray(parsedCandidate)) {
      const records = parsedCandidate.filter(isRecord);
      if (records.length > 0) {
        return records;
      }
    } else if (isRecord(parsedCandidate)) {
      return Object.entries(parsedCandidate).map(([colorCode, value]): Record<string, unknown> => {
        const record = isRecord(value) ? { ...value } : {};
        record.color_code = String(record.color_code ?? record.colorCode ?? colorCode);
        return record;
      });
    }
  }

  return [];
}

function parseJsonLikeValue(value: unknown): unknown {
  let currentValue = value;

  for (let depth = 0; depth < 3 && typeof currentValue === 'string'; depth += 1) {
    try {
      currentValue = JSON.parse(currentValue) as unknown;
    } catch {
      break;
    }
  }

  return currentValue;
}

function getInventoryRows(item: Record<string, unknown>) {
  const candidates = [item.inventory_rows, item.inventoryRows];
  for (const candidate of candidates) {
    const parsedCandidate = parseJsonLikeValue(candidate);
    if (Array.isArray(parsedCandidate)) {
      return parsedCandidate.filter(isRecord);
    }
  }

  return [];
}

function getSavedFabricPrices(savedItem: Record<string, unknown>) {
  const prices: Record<string, Record<string, number>> = {};
  getInventoryRows(savedItem).forEach((row) => {
    const fabricCode = String(row.fabric_code ?? row.fabricCode ?? '').trim();
    const colorCode = String(row.color_code ?? row.colorCode ?? '').trim() || '*';
    const price = Number(row.price ?? row.base_price ?? row.basePrice);
    if (!fabricCode || !Number.isFinite(price) || price <= 0) {
      return;
    }

    prices[fabricCode] ??= {};
    prices[fabricCode][colorCode] ??= Number(price.toFixed(2));
  });
  return prices;
}

function getInventoryRowS3Keys(row: Record<string, unknown>) {
  const parsedValue = parseJsonLikeValue(row.s3_keys ?? row.s3Keys);
  if (!Array.isArray(parsedValue)) {
    return [] as string[];
  }

  return parsedValue.flatMap((entry) => {
    if (!isRecord(entry)) {
      return [];
    }

    const s3Key = normalizeS3ObjectKey(entry.s3_key ?? entry.s3Key ?? entry.objectKey);
    return s3Key ? [s3Key] : [];
  });
}

function getItemS3Keys(item: Record<string, unknown>) {
  const keys = new Set<string>();
  getInventoryRows(item).forEach((row) => {
    getInventoryRowS3Keys(row).forEach((key) => keys.add(key));
  });
  return Array.from(keys);
}

async function refreshSavedItemDetails(savedItem: Record<string, unknown>, itemId: string) {
  const response = await fetchAwsApi(
    `/api/add_items?item_id=${encodeURIComponent(itemId)}&show_deleted=true`,
    { method: 'GET' }
  );
  const responseBody = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(
      responseBody,
      'The item was saved, but its details could not be loaded for image upload.'
    ));
  }

  const refreshedItem = extractSavedItemRecord(responseBody, itemId);
  if (!getSavedItemId(refreshedItem)) {
    return savedItem;
  }

  return refreshedItem;
}

function getSavedColorS3KeysByColorKey(savedItem: Record<string, unknown>) {
  const keysByColorKey = new Map<string, string[]>();
  getInventoryRows(savedItem).forEach((row) => {
    const colorName = String(row.color ?? row.color_name ?? row.colorName ?? '').trim();
    const colorCode = String(row.color_code ?? row.colorCode ?? '').trim();
    const s3Keys = getInventoryRowS3Keys(row);
    if (s3Keys.length === 0) {
      return;
    }

    const rowKey = resolveColorVariantPayloadKey(colorCode, colorName);
    if (!keysByColorKey.has(rowKey)) {
      keysByColorKey.set(rowKey, s3Keys);
    }
  });
  return keysByColorKey;
}

async function persistColorVariantImages<TPhoto extends ColorVariantImageSource>(
  savedItem: Record<string, unknown>,
  variants: Array<SavedItemColorVariantImageInput<TPhoto>>,
  options: SavedItemImageSyncOptions,
  colorKeyedS3Keys: Record<string, ApiColorS3ImageEntry[]>
) {
  const itemId = getSavedItemId(savedItem);
  if (!itemId || variants.length === 0) {
    return;
  }

  const colorVariantsPayload: Record<string, Record<string, unknown>> = {};
  const includedVariantKeys = new Set<string>();

  await Promise.all(
    variants.map(async (variant, index) => {
      const colorName = variant.colorName.trim();
      const colorCode = variant.colorCode?.trim() ?? '';
      const variantKey = resolveColorVariantPayloadKey(colorCode, colorName);
      const swatchSource = variant.swatchImage ?? null;
      const frontImageSource = variant.frontImage ?? null;
      const backImageSource = variant.backImage ?? null;
      let swatchObjectKey: string | null = null;
      let frontImageObjectKey: string | null = null;
      let backImageObjectKey: string | null = null;

      if (swatchSource) {
        const existingObjectKey = getExistingS3ObjectKey(swatchSource);
        swatchObjectKey = existingObjectKey || await uploadColorVariantImageToS3(
          itemId,
          swatchSource,
          `swatches/${slugifyUploadGroupPiece(colorName || colorCode, `variant-${index + 1}`)}`
        );
      }

      if (frontImageSource) {
        const existingObjectKey = getExistingS3ObjectKey(frontImageSource);
        frontImageObjectKey = existingObjectKey || await uploadColorVariantImageToS3(
          itemId,
          frontImageSource,
          `front/${slugifyUploadGroupPiece(colorName || colorCode, `variant-${index + 1}`)}`
        );
      }

      if (backImageSource) {
        const existingObjectKey = getExistingS3ObjectKey(backImageSource);
        backImageObjectKey = existingObjectKey || await uploadColorVariantImageToS3(
          itemId,
          backImageSource,
          `back/${slugifyUploadGroupPiece(colorName || colorCode, `variant-${index + 1}`)}`
        );
      }

      colorVariantsPayload[variantKey] = {
        color_name: normalizeNotApplicableColorValue(colorName),
        price: Number(variant.price.toFixed(2)),
        sort_order: variant.sortOrder ?? index + 1,
        ...(swatchObjectKey ? { color_swatch_img: swatchObjectKey } : {}),
        front_img_s3_key: frontImageObjectKey,
        back_img_s3_key: backImageObjectKey,
        s3_keys: (colorKeyedS3Keys[variantKey] ?? []).map((entry) => ({
          s3_key: entry.s3_key,
          sort_order: entry.sort_order,
        })),
      };
      includedVariantKeys.add(variantKey.toLowerCase());
    })
  );

  const savedS3KeysByColorKey = getSavedColorS3KeysByColorKey(savedItem);
  getColorVariants(savedItem).forEach((variant) => {
    const colorName = String(
      variant.color_name ?? variant.colorName ?? variant.color ?? ''
    ).trim();
    const colorCode = String(variant.color_code ?? variant.colorCode ?? '').trim();
    const variantKey = resolveColorVariantPayloadKey(colorCode, colorName);
    if (includedVariantKeys.has(variantKey.toLowerCase())) {
      return;
    }

    const swatchKey = variant.color_swatch_img
      ?? variant.colorSwatchImg
      ?? variant.color_swatch_s3_key;
    const frontImageKey = variant.front_img_s3_key ?? variant.frontImgS3Key;
    const backImageKey = variant.back_img_s3_key ?? variant.backImgS3Key;
    colorVariantsPayload[variantKey] = {
      color_name: normalizeNotApplicableColorValue(colorName),
      price: (() => {
        const parsedPrice = Number(variant.price);
        return Number.isFinite(parsedPrice) && parsedPrice > 0
          ? Number(parsedPrice.toFixed(2))
          : null;
      })(),
      sort_order: Number(variant.sort_order ?? variant.sortOrder) || Object.keys(colorVariantsPayload).length + 1,
      ...(swatchKey ? { color_swatch_img: normalizeS3ObjectKey(swatchKey) } : {}),
      front_img_s3_key: normalizeS3ObjectKey(frontImageKey) || null,
      back_img_s3_key: normalizeS3ObjectKey(backImageKey) || null,
      s3_keys: (savedS3KeysByColorKey.get(variantKey) ?? []).map((s3Key, index) => ({
        s3_key: s3Key,
        sort_order: index + 1,
      })),
    };
    includedVariantKeys.add(variantKey.toLowerCase());
  });

  const hasColorKeyedS3Keys = Object.keys(colorKeyedS3Keys).length > 0;
  const shouldPersistImages = hasColorKeyedS3Keys
    || Object.values(colorVariantsPayload).some((variant) => (
      variant.color_swatch_img !== undefined
      || variant.front_img_s3_key !== undefined
      || variant.back_img_s3_key !== undefined
      || options.mode === 'sync'
    ));

  if (!shouldPersistImages) {
    return;
  }

  const fabricPrices = getSavedFabricPrices(savedItem);

  const response = await fetchAwsApi('/api/add_items', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      item_id: itemId,
      ...(typeof options.showPublic === 'boolean' ? { show_public: options.showPublic } : {}),
      color_variants: colorVariantsPayload,
      ...(Object.keys(fabricPrices).length > 0 ? { fabric_prices: fabricPrices } : {}),
    }),
  });
  const responseBody = await parseApiResponse(response);

  if (!response.ok) {
    throw new Error(getApiErrorMessage(responseBody, 'Failed to update item images.'));
  }
}

async function deleteItemPhotosFromS3(photoKeys: string[]) {
  if (photoKeys.length === 0) {
    return;
  }

  const response = await fetchFirstAvailableFunctionsApi(
    buildUploadItemPhotoEndpointCandidates(),
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ photoKeys }),
    },
    'Item photo deletion endpoint was not found. Restart the dev server and try again.'
  );
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const failedKeys = Array.isArray(payload.failedKeys) ? payload.failedKeys : [];
    const message = typeof payload.error === 'string'
      ? payload.error
      : typeof payload.message === 'string'
        ? payload.message
        : 'Failed to delete item photos.';
    throw new Error(failedKeys.length > 0 ? `${message} (${failedKeys.join(', ')})` : message);
  }
}

export async function uploadColorVariantImagesToSavedItem<TPhoto extends ColorVariantImageSource>(
  savedItemPayload: unknown,
  variants: Array<SavedItemColorVariantImageInput<TPhoto>>,
  options: SavedItemImageSyncOptions = {}
) {
  const expectedItemId = options.itemId?.trim() ?? '';
  let savedItem = extractSavedItemRecord(savedItemPayload, expectedItemId);
  if (!getSavedItemId(savedItem) && expectedItemId) {
    savedItem = { item_id: expectedItemId };
  }
  const itemId = getSavedItemId(savedItem);

  if (!itemId) {
    throw new Error('Item save response did not include an item ID for image upload.');
  }

  const needsRefresh = options.mode === 'sync'
    ? getColorVariants(savedItem).length === 0 || getInventoryRows(savedItem).length === 0
    : getColorVariants(savedItem).length === 0;
  if (needsRefresh) {
    savedItem = await refreshSavedItemDetails(savedItem, itemId);
  }
  if (!getSavedItemId(savedItem) && expectedItemId) {
    savedItem = { item_id: expectedItemId };
  }

  const photoVariants = variants.filter((variant) => variant.photos.length > 0);
  const colorKeyedS3Keys = await buildColorKeyedS3ImagePayload(itemId, photoVariants);

  await persistColorVariantImages(savedItem, variants, options, colorKeyedS3Keys);

  if (options.mode === 'sync') {
    const currentKeys = new Set(
      Object.values(colorKeyedS3Keys).flatMap((entries) => entries.map((entry) => entry.s3_key))
    );
    const removedKeys = getItemS3Keys(savedItem).filter((key) => !currentKeys.has(key));
    if (removedKeys.length > 0) {
      await deleteItemPhotosFromS3(removedKeys);
    }
  }
}

function buildUploadItemPhotoEndpointCandidates() {
  const legacyAwsApiBaseUrl = sanitizeApiBaseUrl(import.meta.env.VITE_AWS_API_URL);
  return Array.from(new Set([
    ...buildFunctionsApiEndpointCandidates('/api/upload_item_photos'),
    ...buildFunctionsApiEndpointCandidates('/api/upload-item-photos'),
  ])).filter((endpoint) => (
    !legacyAwsApiBaseUrl
    || !endpoint.startsWith(legacyAwsApiBaseUrl)
  ));
}

async function buildUploadFile(source: ColorVariantImageSource, fallbackName: string) {
  if (source.file) {
    return source.file;
  }

  const dataUrl = sanitizeImageUrl(source.previewUrl, false, true);
  if (dataUrl.startsWith('data:image/')) {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], fallbackName, { type: blob.type || 'image/jpeg' });
  }

  const imageUrl = sanitizeImageUrl(source.previewUrl);
  if (!imageUrl) {
    throw new Error('Unable to resolve an item image for upload.');
  }

  const response = await fetch(imageUrl);
  if (!response.ok) {
    throw new Error('Unable to download an existing image for item upload.');
  }

  const blob = await response.blob();
  const mimeType = (blob.type || '').toLowerCase();
  if (mimeType && !mimeType.startsWith('image/')) {
    throw new Error('A saved item image returned an unexpected file type.');
  }

  return new File([blob], fallbackName, { type: mimeType || 'image/jpeg' });
}

async function uploadItemPhotosToS3(
  itemId: string,
  uploadGroup: string,
  sources: ColorVariantImageSource[]
) {
  if (sources.length === 0) {
    return [];
  }

  const files = await Promise.all(
    sources.map((source, index) => buildUploadFile(source, `photo-${index + 1}.jpg`))
  );
  const compressedFiles = await Promise.all(files.map(compressImageForUpload));
  const uploadedObjectKeys: string[] = [];
  let batch: File[] = [];
  let batchBytes = 0;
  let batchIndex = 0;

  const uploadBatch = async (batchFiles: File[], currentBatchIndex: number) => {
    if (batchFiles.length === 0) {
      return;
    }

    const formData = new FormData();
    formData.append('item_id', itemId);
    formData.append(
      'upload_group',
      currentBatchIndex === 0 ? uploadGroup : `${uploadGroup}/batch-${currentBatchIndex + 1}`
    );
    batchFiles.forEach((file) => {
      formData.append('photos', file, file.name || 'photo.jpg');
    });

    const response = await fetchFirstAvailableFunctionsApi(
      buildUploadItemPhotoEndpointCandidates(),
      {
        method: 'POST',
        body: formData,
      },
      'Item photo upload endpoint was not found. Restart the dev server and try again.'
    );
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = typeof payload.error === 'string'
        ? payload.error
        : typeof payload.message === 'string'
          ? payload.message
          : 'Failed to upload item photos.';
      throw new Error(message);
    }

    const photos = Array.isArray(payload.photos) ? payload.photos : [];
    uploadedObjectKeys.push(...photos.map((photo: UploadedItemPhotoResponse) =>
      normalizeS3ObjectKey(photo.objectKey) || normalizeS3ObjectKey(photo.s3_key)
    ));
  };

  try {
    for (const file of compressedFiles) {
      if (batch.length > 0 && batchBytes + file.size > MAX_ITEM_PHOTO_BATCH_BYTES) {
        await uploadBatch(batch, batchIndex);
        batch = [];
        batchBytes = 0;
        batchIndex += 1;
      }

      batch.push(file);
      batchBytes += file.size;
    }

    await uploadBatch(batch, batchIndex);
  } catch (error) {
    if (uploadedObjectKeys.length > 0) {
      await deleteItemPhotosFromS3(uploadedObjectKeys).catch((cleanupError) => {
        console.error('Failed to clean up item photo batches:', cleanupError);
      });
    }
    throw error;
  }

  return uploadedObjectKeys;
}

export async function uploadColorVariantImageToS3(
  itemId: string,
  source: ColorVariantImageSource,
  uploadGroup: string
) {
  const existingObjectKey = getExistingS3ObjectKey(source);
  if (existingObjectKey) {
    return existingObjectKey;
  }

  const [uploadedObjectKey] = await uploadItemPhotosToS3(itemId, uploadGroup, [source]);
  if (!uploadedObjectKey) {
    throw new Error('Item photo upload did not return an object key.');
  }
  return uploadedObjectKey;
}

export async function buildColorKeyedS3ImagePayload<TPhoto extends ColorVariantImageSource>(
  itemId: string,
  variants: Array<ColorVariantImageInput<TPhoto>>
) {
  const entries = await Promise.all(
    variants.map(async (variant) => {
      const colorKey = resolveColorVariantPayloadKey(variant.colorCode, variant.colorName);
      const uploadGroup = slugifyUploadGroupPiece(variant.colorName, 'not-applicable');
      const missingUploadSources: TPhoto[] = [];
      const existingKeysByIndex = new Map<number, string>();

      variant.photos.forEach((photo, index) => {
        const existingObjectKey = getExistingS3ObjectKey(photo);
        if (existingObjectKey) {
          existingKeysByIndex.set(index, existingObjectKey);
        } else {
          missingUploadSources.push(photo);
        }
      });

      const uploadedKeys = await uploadItemPhotosToS3(itemId, uploadGroup, missingUploadSources);
      let uploadedIndex = 0;
      const photos = variant.photos.map((_, index): ApiColorS3ImageEntry => {
        const s3Key = existingKeysByIndex.get(index) ?? uploadedKeys[uploadedIndex++];
        if (!s3Key) {
          throw new Error('Item photo upload did not return an object key.');
        }
        return {
          s3_key: s3Key,
          is_primary: index === 0,
          sort_order: index + 1,
        };
      });

      return [colorKey, photos] as const;
    })
  );

  return Object.fromEntries(entries);
}

export function reorderItemsById<T extends { id: string }>(
  items: T[],
  draggedItemId: string,
  targetItemId: string
) {
  if (draggedItemId === targetItemId) {
    return items;
  }

  const sourceIndex = items.findIndex((item) => item.id === draggedItemId);
  const targetIndex = items.findIndex((item) => item.id === targetItemId);
  if (sourceIndex < 0 || targetIndex < 0) {
    return items;
  }

  const nextItems = items.slice();
  const [draggedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(targetIndex, 0, draggedItem);
  return nextItems;
}
